import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Modal from "../common_modal/Modal.jsx";

// Views
import C_V_TEXT from "../c_view/C_V_TEXT.jsx";
import C_V_FORMULA from "../c_view/C_V_FORMULA.jsx";
import C_V_FTEXT from "../c_view/C_V_FTEXT.jsx";
import C_V_IMG from "../c_view/C_V_IMG.jsx";

// Editors
import C_E_TEXTAREA from "../c_edit/C_E_TEXTAREA.jsx";
import C_E_NUMBER from "../c_edit/C_E_NUMBER.jsx";
import C_E_PHONE from "../c_edit/C_E_PHONE.jsx";
import C_E_SELECT from "../c_edit/C_E_SELECT.jsx";
import C_E_MULTI from "../c_edit/C_E_MULTI.jsx";
import C_E_DOC from "../c_edit/C_E_DOC.jsx";
import C_E_DATE from "../c_edit/C_E_DATE.jsx";
import C_E_TIME from "../c_edit/C_E_TIME.jsx";
import C_E_YEAR from "../c_edit/C_E_YEAR.jsx";
import C_E_RANGE from "../c_edit/C_E_RANGE.jsx";
import C_E_LINK from "../c_edit/C_E_LINK.jsx";
import C_E_USER from "../c_edit/C_E_USER.jsx";
import C_E_CITY from "../c_edit/C_E_CITY.jsx";
import C_E_ADDRESS from "../c_edit/C_E_ADDRESS.jsx";
import C_E_LATLNG from "../c_edit/C_E_LATLNG.jsx";
import C_E_TABLE from "../c_edit/C_E_TABLE.jsx";

import {
  set_value_layer,
  editProperties,
  editPropertiesV2,
  deleteFeature,
} from "../../App/actions/layerActions";
import { packThePropertiesDataToUseApiEdit } from "../../App/validation/convert_data";

// Added data_types to get the icons
import { data_types } from "../../App/validation/data_types";

const POPUP_EDITOR = ({ fields_selected }) => {
  const dispatch = useDispatch();

  const layer_state = useSelector((state) => state.layer);
  const auth_state = useSelector((state) => state.auth);
  const properties_state = useSelector((state) => state.properties);

  const mode_map = properties_state.mode_map; //editor || viewer

  const {
    modal_edit_feature_properties,
    layer_id_popup,
    feature_object_selected,
    geo_layer_list,
    popup_dom,
  } = layer_state;

  const language = localStorage?.language || auth_state?.language || "id";

  const [values, setValues] = useState(() => {
    const map = {};
    fields_selected?.forEach((field) => {
      map[field.key] = feature_object_selected?.properties?.[field.key] ?? "";
    });
    return map;
  });

  const [expandedSections, setExpandedSections] = useState({});
  const [tableModalConfig, setTableModalConfig] = useState(null);
  const [modal_delete_feature, set_modal_delete_feature] = useState(false);

  useEffect(() => {
    if (!feature_object_selected) return;
    const map = {};
    fields_selected?.forEach((field) => {
      map[field.key] = feature_object_selected?.properties?.[field.key] ?? "";
    });
    setValues(map);
  }, [feature_object_selected, fields_selected]);

  const fields_final = useMemo(() => {
    return (fields_selected || []).filter(
      (field) => !field?.parent_uuid && !field?.isStyle,
    );
  }, [fields_selected]);

  const sectionKeys = useMemo(() => {
    return fields_final.filter((f) => f.type === "section").map((f) => f.key);
  }, [fields_final]);

  const hasSections = sectionKeys.length > 0;

  useEffect(() => {
    setExpandedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      sectionKeys.forEach((key) => {
        if (next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sectionKeys]);

  const groupedFields = useMemo(() => {
    const groups = [];
    let currentSection = null;
    let currentChildren = [];

    fields_final.forEach((field) => {
      if (field.type === "section") {
        if (currentSection || currentChildren.length > 0) {
          groups.push({ section: currentSection, children: currentChildren });
        }
        currentSection = field;
        currentChildren = [];
      } else {
        currentChildren.push(field);
      }
    });

    if (currentSection || currentChildren.length > 0) {
      groups.push({ section: currentSection, children: currentChildren });
    }

    return groups;
  }, [fields_final]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    const all = {};
    sectionKeys.forEach((k) => (all[k] = true));
    setExpandedSections(all);
  };

  const collapseAll = () => {
    const none = {};
    sectionKeys.forEach((k) => (none[k] = false));
    setExpandedSections(none);
  };

  const close_modal_fullscreen = () => {
    dispatch(
      set_value_layer({
        key: "modal_edit_feature_properties",
        value: false,
      }),
    );
  };

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSaveData = (fieldKey, newValue, oldValue) => {
    if (String(oldValue ?? "") === String(newValue ?? "")) return;

    const geo_layer = geo_layer_list?.find(
      (item) => item.geo_layer._id === layer_id_popup,
    )?.geo_layer;

    if (geo_layer && feature_object_selected) {
      const editFn =
        geo_layer.architecture === "v2" ? editPropertiesV2 : editProperties;
      const user = auth_state?.user;

      const updatedRow = {
        ...feature_object_selected,
        properties: {
          ...feature_object_selected.properties,
          [fieldKey]: newValue,
        },
      };

      const params = {
        id: feature_object_selected.id,
        field: fieldKey,
        row: updatedRow,
        value: newValue,
      };

      const body = packThePropertiesDataToUseApiEdit(layer_id_popup, params);

      const updated_at_key = geo_layer.fields?.find(
        (item) => !item?.parent_uuid && item?.type === "last_edited",
      )?.key;
      const updated_by_key = geo_layer.fields?.find(
        (item) => !item?.parent_uuid && item?.type === "edited_by",
      )?.key;

      if (updated_at_key) {
        body.updated_at_properties_key = updated_at_key;
        body.updated_at_properties_value = new Date();
      }
      if (updated_by_key) {
        body.updated_by_properties_key = updated_by_key;
        body.updated_by_properties_value = user?.name;
      }

      dispatch(editFn(body));
    }
  };

  const on_save_city = (rowid, fieldkey, value_new, value_old) => {
    const geo_layer = geo_layer_list?.find(
      (item) => item.geo_layer._id === layer_id_popup,
    )?.geo_layer;

    const editFn =
      geo_layer.architecture === "v2" ? editPropertiesV2 : editProperties;

    const feature_key = feature_object_selected?.key;
    const properties_key = fieldkey;
    const properties_value = value_new;

    const body = {
      geo_layer_id: layer_id_popup,
      feature_key,
      properties_key,
      properties_value,
    };
    dispatch(editFn(body));
  };

  const handleBlur = (key, oldValue) => {
    handleSaveData(key, values[key], oldValue);
  };

  const handleKeyDown = (e, key, oldValue) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveData(key, values[key], oldValue);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setValues((prev) => ({ ...prev, [key]: oldValue ?? "" }));
    }
  };

  const handle_delete_feature = () => {
    const content = {
      feature_key: feature_object_selected?.key,
      geo_layer_id: layer_id_popup,
    };

    dispatch(deleteFeature(content)).then(() => {
      set_modal_delete_feature(false);
      close_modal_fullscreen();
      if (popup_dom) {
        popup_dom.remove();
        dispatch(
          set_value_layer({
            key: "popup_dom",
            value: null,
          }),
        );
      }
    });
  };

  const renderField = (field, field_index) => {
    const value = feature_object_selected?.properties?.[field?.key];
    let input_content;

    const type_current = data_types(language).find(
      (type_element) => type_element.value === field.type,
    );
    const icon_type = type_current?.icon;

    const isNumberFormula = [
      "counting_poi",
      "counting_custom",
      "calculate_dimension",
      "math_operators",
      "rt",
      "rw",
      "table_formula",
    ].includes(field?.type);

    const isTextFormula = [
      "simple_referring",
      "2d_referring",
      "template_literal",
      "conditional_statement",
      "id_data",
      "last_edited",
      "edited_by",
    ].includes(field?.type);

    if (isNumberFormula) {
      input_content = <C_V_FORMULA value={value} inPopup={true} />;
    } else if (isTextFormula) {
      input_content = <C_V_FTEXT value={value} inPopup={true} />;
    }
    // IMAGE HANDLING
    else if (field?.type === "image") {
      input_content = (
        <C_V_IMG
          value={value}
          row={feature_object_selected}
          field={field}
          inPopup={true} // Triggers reveal mode
          language={language}
        />
      );
    } else if (field?.type === "number" || field?.type === "currency") {
      input_content = (
        <C_E_NUMBER
          value={value}
          tempValue={values[field.key] ?? ""}
          isEditing={true}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "phone_number") {
      input_content = (
        <C_E_PHONE
          value={value}
          tempValue={values[field.key] ?? ""}
          isEditing={true}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "selection" || field?.type === "radio_button") {
      let processedField = field;
      const parent_key = field.parent_array_templates_key;

      if (parent_key) {
        const parent_val = feature_object_selected?.properties?.[parent_key];
        if (parent_val) {
          processedField = {
            ...field,
            array_templates: (field.array_templates || []).filter(
              (o) => o.parent === parent_val,
            ),
          };
        }
      }
      input_content = (
        <C_E_SELECT
          value={value}
          tempValue={values[field.key] ?? ""}
          onChange={(e) => {
            handleChange(field.key)(e);
            handleSaveData(field.key, e.target.value, value);
          }}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          field={processedField}
          inPopup={true}
        />
      );
    } else if (field?.type === "checklist") {
      let processedField = field;
      const parent_key = field.parent_array_templates_key;
      if (parent_key) {
        const parent_val = feature_object_selected?.properties?.[parent_key];
        if (parent_val) {
          processedField = {
            ...field,
            array_templates: (field.array_templates || []).filter(
              (o) => o.parent === parent_val,
            ),
          };
        }
      }
      input_content = (
        <C_E_MULTI
          value={value}
          tempValue={values[field.key] ?? ""}
          onChange={(e) => {
            handleChange(field.key)(e);
            handleSaveData(field.key, e.target.value, value);
          }}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          field={processedField}
          inPopup={true}
        />
      );
    } else if (field?.type === "document") {
      input_content = (
        <C_E_DOC
          row={feature_object_selected}
          field={field}
          inPopup={true}
          language={language}
          onSave={(rowId, fKey, newValue, oldValue) =>
            handleSaveData(fKey, newValue, oldValue)
          }
        />
      );
    } else if (
      field?.type === "kelurahan" ||
      field?.type === "kecamatan" ||
      field?.type === "kota" ||
      field?.type === "provinsi"
    ) {
      input_content = (
        <C_E_CITY
          config={{
            row: feature_object_selected,
            field: field,
            initialValue: value,
          }}
          liveValue={value}
          onSave={on_save_city}
          inPopup={true}
          language={language}
        />
      );
    } else if (
      field?.type === "auto_address" ||
      field?.type === "auto_address_poi"
    ) {
      input_content = (
        <C_E_ADDRESS
          config={{
            row: feature_object_selected,
            field: field,
            initialValue: value,
          }}
          liveValue={value}
          onSave={on_save_city}
          inPopup={true}
          language={language}
        />
      );
    } else if (field?.type === "latitude" || field?.type === "longitude") {
      input_content = (
        <C_E_LATLNG
          config={{
            row: feature_object_selected,
            field: field,
            initialValue: value,
          }}
          inPopup={true}
          language={language}
        />
      );
    } else if (field?.type === "table") {
      input_content = (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            height: "100%",
          }}
        >
          <p
            className="container background_white"
            style={{
              margin: 0,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={field?.name || "Tabel"}
          >
            Tabel
          </p>
          {mode_map === "editor" && (
            <button
              className="button background_black"
              onClick={(e) => {
                e.stopPropagation();
                setTableModalConfig({
                  row: feature_object_selected,
                  field: field,
                  child_array: feature_object_selected?.child_array,
                  isNested: !!feature_object_selected?.parent_uuid_temp,
                });
              }}
            >
              Buka tabel
            </button>
          )}
        </div>
      );
    } else if (field?.type === "date") {
      input_content = (
        <C_E_DATE
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "time") {
      input_content = (
        <C_E_TIME
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "year") {
      input_content = (
        <C_E_YEAR
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "textarea") {
      input_content = (
        <C_E_TEXTAREA
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          rowId={feature_object_selected?.id}
          fieldKey={field?.key}
          inPopup={true}
        />
      );
    } else if (field?.type === "range") {
      input_content = (
        <C_E_RANGE
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          min={field?.min ?? 1}
          max={field?.max ?? 100}
          step={field?.step ?? 1}
          inPopup={true}
        />
      );
    } else if (field?.type === "link") {
      input_content = (
        <C_E_LINK
          tempValue={values[field.key] ?? ""}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    } else if (field?.type === "auto_generate_user_name") {
      input_content = (
        <C_E_USER
          config={{ row: feature_object_selected, field: field }}
          inPopup={true}
        />
      );
    } else {
      input_content = (
        <C_V_TEXT
          value={value}
          tempValue={values[field.key] ?? ""}
          isEditing={true}
          onChange={handleChange(field.key)}
          onBlur={() => handleBlur(field.key, value)}
          onKeyDown={(e) => handleKeyDown(e, field.key, value)}
          inPopup={true}
        />
      );
    }

    return (
      <div key={field_index} className="margin_bottom">
        <div className="container background_grey_light margin_bottom">
          <div
            className="margin_bottom"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <p
              className="text_medium sub_color"
              style={{ margin: 0, flex: 1, wordBreak: "break-word" }}
            >
              {field?.name}
            </p>
            {icon_type && (
              <img
                src={icon_type}
                alt={field.type}
                style={{
                  width: "18px",
                  height: "18px",
                  objectFit: "contain",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              />
            )}
          </div>
          {input_content}
        </div>
      </div>
    );
  };

  let content;

  if (modal_edit_feature_properties) {
    content = (
      <main>
        {hasSections && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            <button
              onClick={expandAll}
              className="badge_small hover_primary cursor_pointer"
              style={{ background: "#f1f1f1", border: "none", color: "#333" }}
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="badge_small hover_primary cursor_pointer"
              style={{ background: "#f1f1f1", border: "none", color: "#333" }}
            >
              Collapse All
            </button>
          </div>
        )}

        {groupedFields.map((group, group_index) => {
          const isExpanded =
            !group.section || expandedSections[group.section.key];

          const section_icon = group.section
            ? data_types(language).find((t) => t.value === group.section.type)
                ?.icon
            : null;

          return (
            <div key={`group-${group_index}`} className="margin_bottom">
              {group.section && (
                <div
                  className="badge_small green_container margin_top margin_bottom"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSection(group.section.key)}
                >
                  <span style={{ flex: 1, wordBreak: "break-word" }}>
                    {group.section.name}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {section_icon && (
                      <img
                        src={section_icon}
                        alt={group.section.type}
                        style={{
                          width: "18px",
                          height: "18px",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
              )}

              {isExpanded && (
                <div
                  style={{
                    paddingLeft: group.section ? "8px" : "0px",
                    borderLeft: group.section ? "2px solid #e0e0e0" : "none",
                    marginLeft: group.section ? "8px" : "0px",
                  }}
                >
                  {group.children.map((childField, child_index) =>
                    renderField(childField, `${group_index}-${child_index}`),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </main>
    );
  }

  const modal_delete_feature_content = modal_delete_feature && (
    <Modal
      modalSize="small"
      id="modal_delete"
      isOpen={modal_delete_feature}
      onClose={() => set_modal_delete_feature(false)}
      title={
        language === "ina" || language === "id"
          ? "Apakah Anda yakin ingin menghapus fitur ini?"
          : "Are you sure to delete this feature?"
      }
    >
      <div className="box-body">
        <p className="text_bold">
          {language === "ina" || language === "id"
            ? "Proses ini tidak dapat dibatalkan."
            : "This process can't be undone."}
        </p>
        <br />
        <br />
        <br />
        <button
          className="button hover_danger"
          style={{ marginTop: "1rem" }}
          onClick={handle_delete_feature}
        >
          {language === "ina" || language === "id" ? "Hapus" : "Delete"}
        </button>
      </div>
    </Modal>
  );

  const modal_fullscreen_content = modal_edit_feature_properties && (
    <Modal
      title="Edit detail"
      modalSize="small"
      isOpen={modal_edit_feature_properties}
      onClose={close_modal_fullscreen}
    >
      <div className="box-body">
        {mode_map === "editor" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "16px",
            }}
          >
            <button
              className="button hover_danger"
              onClick={() => set_modal_delete_feature(true)}
            >
              Delete
            </button>
          </div>
        )}
        {content}
      </div>
    </Modal>
  );

  return (
    <>
      {modal_fullscreen_content}
      {modal_delete_feature_content}
      {tableModalConfig && (
        <C_E_TABLE
          config={tableModalConfig}
          onClose={() => setTableModalConfig(null)}
        />
      )}
    </>
  );
};

export default POPUP_EDITOR;
